import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Tag, Space, Select, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EyeOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import stockTransferService, { type StockTransfer } from '@/services/stockTransfer.service';
import { useWarehouses } from '@/hooks/useInventory';
import { useAuthStore } from '@/store/authStore';
import { isPlatformSuperAdmin } from '@/utils/authUser';

const { Title, Text } = Typography;

const statusColor = (s: string) => {
  const map: Record<string, string> = {
    Draft: 'default',
    'Pending Approval': 'warning',
    Approved: 'success',
    'In Transit': 'processing',
    Received: 'green',
    Cancelled: 'error',
  };
  return map[s] || 'default';
};

export const StockTransfersPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canCreate =
    isPlatformSuperAdmin(user) || user?.permissions?.includes('inventory.transfer.create');
  const [status, setStatus] = useState<string>('');
  const [fromWh, setFromWh] = useState<string>('');
  const [toWh, setToWh] = useState<string>('');
  const { data: warehouses = [] } = useWarehouses();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['stock-transfers', status, fromWh, toWh],
    queryFn: () =>
      stockTransferService.list({
        status: status || undefined,
        fromWarehouseId: fromWh || undefined,
        toWarehouseId: toWh || undefined,
      }),
  });

  const whName = (id: string) => {
    const w = warehouses.find((x) => x.id === id);
    return w?.warehouse_name || w?.warehouse_code || id?.slice(0, 8) || '—';
  };

  const columns: ColumnsType<StockTransfer> = useMemo(
    () => [
      {
        title: 'Transfer #',
        dataIndex: 'transfer_number',
        width: 140,
        render: (t: string) => <Text strong>{t}</Text>,
      },
      {
        title: 'Date',
        dataIndex: 'transfer_date',
        width: 110,
        render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY') : '—'),
      },
      {
        title: 'From',
        dataIndex: 'from_warehouse_id',
        width: 160,
        ellipsis: true,
        render: (id: string) => whName(id),
      },
      {
        title: 'To',
        dataIndex: 'to_warehouse_id',
        width: 160,
        ellipsis: true,
        render: (id: string) => whName(id),
      },
      {
        title: 'Status',
        dataIndex: 'transfer_status',
        width: 130,
        render: (s: string) => <Tag color={statusColor(s)}>{s}</Tag>,
      },
      {
        title: 'Lines',
        width: 70,
        render: (_: unknown, r) => r.items?.length ?? 0,
      },
      {
        title: 'Actions',
        width: 100,
        fixed: 'right',
        render: (_: unknown, r) => (
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/scm/warehouse/stock-transfer/${r.id}`)}
          >
            Open
          </Button>
        ),
      },
    ],
    [warehouses, navigate]
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Stock Transfers
          </Title>
          <Text type="secondary">Move stock between warehouses (approve → ship → receive)</Text>
        </div>
        {canCreate ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/scm/warehouse/stock-transfer/new')}
          >
            New transfer
          </Button>
        ) : null}
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            onChange={(v) => setStatus(v || '')}
            options={[
              'Draft',
              'Pending Approval',
              'Approved',
              'In Transit',
              'Received',
              'Cancelled',
            ].map((s) => ({ value: s, label: s }))}
          />
          <Select
            allowClear
            placeholder="From warehouse"
            style={{ width: 200 }}
            showSearch
            optionFilterProp="label"
            onChange={(v) => setFromWh(v || '')}
            options={warehouses.map((w) => ({
              value: w.id,
              label: w.warehouse_name || w.warehouse_code,
            }))}
          />
          <Select
            allowClear
            placeholder="To warehouse"
            style={{ width: 200 }}
            showSearch
            optionFilterProp="label"
            onChange={(v) => setToWh(v || '')}
            options={warehouses.map((w) => ({
              value: w.id,
              label: w.warehouse_name || w.warehouse_code,
            }))}
          />
        </Space>
      </Card>

      <Card>
        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 1000 }}
          pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        />
      </Card>
    </div>
  );
};

export default StockTransfersPage;
